import { Paragraph } from "#components/Text";
import { useColors } from "#components/useColors";
import { Box, Button } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";

class Cancelled extends Error {}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const flushStyles = (el: HTMLElement) => {
  el.getBoundingClientRect();
};

const shuffled = <T,>(items: T[]) => {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

export const Subsection = ({
  children,
  number,
}: {
  children: React.ReactNode;
  number: number;
}) => (
  <Box
    className="subsection"
    data-number={number}
    id={`subsection-${number}`}
    overflow="hidden"
    sx={{
      transition: `
        max-height var(--slot-duration, 1s) ease-in-out,
        transform var(--slot-duration, 1s) ease-in-out
      `,
      "& > p:first-of-type": {
        marginTop: 0,
      },
      "& > h2": {
        marginTop: 0,
        marginBottom: "0.9rem",
        overflow: "hidden",
        transition: `
          max-height var(--slot-duration, 1s) ease-in-out,
          margin-bottom var(--slot-duration, 1s) ease-in-out
        `,
      },
    }}
  >
    {children}
  </Box>
);

export const SlotMachine = ({ children }: { children: React.ReactNode }) => {
  const { backgroundColor } = useColors();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pastTheEndRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [rolls, setRolls] = useState(0);
  const [rolling, setRolling] = useState(false);
  const rollingRef = useRef(false);
  const cancelledRef = useRef(false);
  const chanceOfJackpot =
    10 + (rolls > 0 ? 20 : 0) + (rolls > 1 ? 30 : 0) + (rolls > 2 ? 40 : 0);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }
    let width = wrapper.offsetWidth;
    const observer = new ResizeObserver(() => {
      if (wrapper.offsetWidth === width) {
        return;
      }
      width = wrapper.offsetWidth;
      cancelledRef.current = true;
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  const roll = async () => {
    if (rollingRef.current) {
      return;
    }
    rollingRef.current = true;
    cancelledRef.current = false;
    setRolling(true);

    const wrapper = wrapperRef.current!;
    const speedUp = Math.min(1 + rolls * 0.35, 2);
    const duration = 1000 / speedUp;
    const stagger = 100 / speedUp;

    const rows = Array.from(
      wrapper.querySelectorAll<HTMLElement>(".subsection"),
    )
      .sort(
        // flex order, not dom order:
        (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
      )
      .map((el: HTMLElement) => {
        return {
          el,
          heading: el.querySelector<HTMLElement>(":scope > h2"),
          height: el.getBoundingClientRect().height,
          headingHeight:
            el
              .querySelector<HTMLElement>(":scope > h2")
              ?.getBoundingClientRect().height ?? 0,
        };
      });

    const jackpot = Math.random() < chanceOfJackpot / 100;
    const next = jackpot
      ? [...rows].sort(
          (a, b) => Number(a.el.dataset.number) - Number(b.el.dataset.number),
        )
      : shuffled(rows);

    const finish = () => {
      // remove props so failures recover:
      next.forEach(({ el, heading }, i) => {
        el.style.order = String(i);
        el.style.removeProperty("max-height");
        el.style.removeProperty("transform");
        el.style.removeProperty("transition");
        heading?.style.removeProperty("max-height");
        heading?.style.removeProperty("margin-bottom");
      });
      wrapper.style.removeProperty("--slot-duration");
      document.documentElement.style.removeProperty("overflow-anchor");
      setRolls((r) => r + 1);
      rollingRef.current = false;
      setRolling(false);
    };

    const step = async (ms: number) => {
      await wait(ms);
      if (cancelledRef.current) {
        throw new Cancelled();
      }
    };

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      finish();
      return;
    }

    try {
      wrapper.style.setProperty("--slot-duration", `${duration}ms`);

      // prevent scroll anchoring:
      document.documentElement.style.overflowAnchor = "none";
      wrapper.scrollIntoView({ behavior: "smooth", block: "start" });
      await step(300);

      // pin height
      for (const { el, heading, height, headingHeight } of rows) {
        el.style.maxHeight = `${height}px`;
        if (heading) {
          heading.style.maxHeight = `${headingHeight}px`;
        }
      }
      flushStyles(wrapper);

      // collapse
      for (const { el, heading } of rows) {
        el.style.maxHeight = "1.5em";
        if (heading) {
          heading.style.maxHeight = "0px";
          heading.style.marginBottom = "0px";
        }
        await step(stagger);
      }
      await step(duration);

      // reorder
      const before = rows.map(({ el }) => el.getBoundingClientRect().top);
      next.forEach(({ el }, i) => {
        el.style.order = String(i);
      });
      const after = rows.map(({ el }) => el.getBoundingClientRect().top);
      rows.forEach(({ el }, i) => {
        el.style.transition = "none";
        el.style.transform = `translateY(${before[i] - after[i]}px)`;
      });
      flushStyles(wrapper);
      rows.forEach(({ el }) => {
        el.style.removeProperty("transition");
        el.style.removeProperty("transform");
      });
      await step(duration);

      // expand
      for (const { el, heading, height, headingHeight } of next) {
        el.style.maxHeight = `${height}px`;
        if (heading) {
          heading.style.maxHeight = `${headingHeight}px`;
          heading.style.removeProperty("margin-bottom");
        }
        await step(stagger);
      }
      await step(duration);
    } catch (error) {
      if (!(error instanceof Cancelled)) {
        throw error;
      }
    } finally {
      finish();
    }
  };

  useEffect(() => {
    const pastTheEnd = pastTheEndRef.current;
    const button = buttonRef.current;
    if (!pastTheEnd || !button) {
      return;
    }
    const observer = new IntersectionObserver(([{ isIntersecting }]) => {
      if (isIntersecting) {
        button.style.position = "sticky";
        button.style.opacity = "1";
      } else {
        button.style.opacity = "";
      }
    });
    observer.observe(pastTheEnd);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Box ref={wrapperRef} display="flex" flexDirection="column">
        {children}
      </Box>
      <Box ref={pastTheEndRef} />
      <Box
        ref={buttonRef}
        bg={backgroundColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap="parSpace"
        bottom={0}
        position="static"
        opacity={rolling ? 1 : 0.2}
        transition="opacity 0.3s"
        _hover={{ opacity: 1 }}
      >
        <Button
          isDisabled={rolling}
          onClick={() => {
            roll();
          }}
          flex={1}
          my="parSpace"
          variant="outline"
        >
          Shuffle
        </Button>
        <Paragraph color="" fontFamily="sans" textAlign="center">
          Chance of jackpot: {chanceOfJackpot}%.
        </Paragraph>
      </Box>
    </>
  );
};
